import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toOptions } from '@/shared/lib/options';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import { useListFilters } from '@/shared/lib/useListFilters';
import { FilterActions } from '@/shared/ui/FilterActions';
import { FilterField } from '@/shared/ui/FilterField';
import { FilterSelect } from '@/shared/ui/FilterSelect';
import { Pagination } from '@/shared/ui/Pagination';
import { QueryStatus } from '@/shared/ui/QueryStatus';
import { SortableTh } from '@/shared/ui/SortableTh';
import {
  fetchActiveSeoCities,
  fetchActiveSeoMarks,
  fetchActiveSeoModels,
  fetchActiveSeoWorks,
  fetchSeoPages,
  type SeoPage,
} from './api';

const DEFAULT_SORT = '+url';

const emptyFilters = { url: '', workId: '', modelId: '', markId: '', cityId: '' };

export const SeoPagesPage = () => {
  const list = useListFilters(emptyFilters);
  const { draft, applied, page, sortField, sortOrder } = list;

  const works = useQuery({ queryKey: ['seo-active-works'], queryFn: fetchActiveSeoWorks });
  const models = useQuery({ queryKey: ['seo-active-models'], queryFn: fetchActiveSeoModels });
  const marks = useQuery({ queryKey: ['seo-active-marks'], queryFn: fetchActiveSeoMarks });
  const cities = useQuery({ queryKey: ['seo-active-cities'], queryFn: fetchActiveSeoCities });

  const params = {
    page,
    sort: sortField ? `${sortOrder === 'asc' ? '+' : '-'}${sortField}` : DEFAULT_SORT,
    parent_id: applied.workId,
    model_id: applied.modelId,
    mark_id: applied.markId,
    city_id: applied.cityId,
    url: applied.url,
  };
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['seo-pages', params],
    queryFn: () => fetchSeoPages(params),
  });
  const rows = getListRows<SeoPage>(data, ['seo_pages']);

  const selectField = (
    label: string,
    key: 'workId' | 'modelId' | 'markId' | 'cityId',
    query: typeof works,
  ) => (
    <FilterField label={label}>
      <FilterSelect
        options={toOptions(query.data)}
        value={draft[key]}
        isLoading={query.isLoading}
        onChange={(v) => list.setField(key, v)}
      />
    </FilterField>
  );

  return (
    <>
      <section className="content-header">
        <h1>Страницы</h1>
      </section>
      <section className="content">
        <div className="box">
          <div className="box-header with-border">
            <h3 className="box-title">Услуги</h3>
            <form onSubmit={list.submit} style={{ marginTop: 12 }}>
              <div className="row">
                <div className="col-lg-4">
                  <FilterField label="Поиск по url">
                    <input
                      className="form-control input-sm"
                      style={{ width: 250 }}
                      placeholder="Поиск..."
                      value={draft.url}
                      onChange={(e) => list.setField('url', e.target.value)}
                    />
                  </FilterField>
                  {selectField('Поиск по услуге', 'workId', works)}
                </div>
                <div className="col-lg-4">
                  {selectField('Поиск по модели', 'modelId', models)}
                  {selectField('Поиск по марке', 'markId', marks)}
                </div>
                <div className="col-lg-4">{selectField('Поиск по городу', 'cityId', cities)}</div>
              </div>
              <FilterActions disabled={isFetching} onReset={list.reset} />
            </form>
          </div>
          <div className="box-body">
            <QueryStatus isLoading={isLoading} error={error} errorText="Ошибка загрузки страниц" />
            <table className="table table-bordered">
              <thead>
                <tr>
                  <SortableTh active={sortField === 'id'} onClick={() => list.toggleSort('id')}>
                    id
                  </SortableTh>
                  <th>Title</th>
                  <th>Description</th>
                  <th>H1</th>
                  <SortableTh active={sortField === 'url'} onClick={() => list.toggleSort('url')}>
                    URL страницы
                  </SortableTh>
                  <th>Продающий SEO текст</th>
                  <th>SEO текст</th>
                  <th>Edit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((seoPage) => (
                  <tr key={seoPage.id}>
                    <td>{seoPage.id}</td>
                    <td>{seoPage.title}</td>
                    <td>{seoPage.description}</td>
                    <td>{seoPage.short_description}</td>
                    <td>{seoPage.url}</td>
                    {/* HTML authored in the admin panel; rendered as-is like in legacy. */}
                    <td dangerouslySetInnerHTML={{ __html: seoPage.selling_seo_text ?? '' }} />
                    <td dangerouslySetInnerHTML={{ __html: seoPage.seo_text ?? '' }} />
                    <td>
                      <Link to={`/seo/pages/edit/${seoPage.id}`} className="btn btn-success btn-sm">
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} totalPages={getTotalPages(data)} onChange={list.setPage} />
          </div>
        </div>
      </section>
    </>
  );
};
